/**
 * Product Pricing Catalog — Pantalla A · Lista de Productos.
 * Sprint P1: Sólo Business Cards.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { productCatalogApi } from "./api";
import { useAuth } from "@/context/AuthContext";

export default function ProductCatalogListPage({ onOpenProduct }) {
  const { isAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", category: "", photo_url: "", active: true });
  const [catChoice, setCatChoice] = useState("");
  const [search, setSearch] = useState("");

  // Búsqueda client-side: nombre + categoría + nombres de tipos,
  // insensible a mayúsculas y acentos. Sin backend.
  const norm = (s) =>
    String(s || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  const q = norm(search.trim());
  const qc = q.replace(/\s+/g, "");
  const hit = (v) => {
    const n = norm(v);
    return n.includes(q) || n.replace(/\s+/g, "").includes(qc);
  };
  const visibleProducts = !q
    ? products
    : products.filter(
        (p) =>
          hit(p.name) ||
          hit(p.category) ||
          (p.types || []).some((t) => hit(t.name))
      );

  // Agrupación visual por `category` (campo existente, sin backend nuevo).
  const groupProducts = (list) => {
    const map = new Map();
    for (const p of list) {
      const key = (p.category || "").trim() || "Sin categoría";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    return Array.from(map.entries()).sort((a, b) => {
      if (a[0] === "Sin categoría") return 1;
      if (b[0] === "Sin categoría") return -1;
      return a[0].localeCompare(b[0]);
    });
  };
  const existingCategories = Array.from(
    new Set(products.map((p) => (p.category || "").trim()).filter(Boolean))
  ).sort();

  const load = async () => {
    setLoading(true);
    try {
      setProducts(await productCatalogApi.list());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      const prod = await productCatalogApi.create(form);
      toast.success("Producto creado");
      setCreating(false);
      setForm({ name: "", category: "", photo_url: "", active: true });
      setCatChoice("");
      await load();
      onOpenProduct?.(prod.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const removeProduct = async (id) => {
    if (!window.confirm("¿Eliminar este producto y toda su matriz de precios?")) return;
    try {
      await productCatalogApi.remove(id);
      toast.success("Eliminado");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  return (
    <div data-testid="product-catalog-list" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-overline">CAT / PC</div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
            Catálogo de Productos
          </h2>
          <p className="text-sm text-[var(--text-secondary)]">
            Costos y precios por combinación Tipo × Cantidad.
          </p>
        </div>
        {!creating && isAdmin && (
          <button
            data-testid="product-catalog-new-btn"
            onClick={() => setCreating(true)}
            className="bg-black text-white px-5 py-2.5 font-display font-bold tracking-wide hover:bg-[var(--primary)] transition-colors"
          >
            + Nuevo Producto
          </button>
        )}
      </div>

      {creating && isAdmin && (
        <form
          data-testid="product-catalog-new-form"
          onSubmit={submitCreate}
          className="border-2 border-black bg-white p-4 space-y-3"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label-overline block mb-1">Nombre</label>
              <input
                data-testid="pc-new-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-[var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-black"
                placeholder="Business Cards"
              />
            </div>
            <div>
              <label className="label-overline block mb-1">Categoría</label>
              <select
                data-testid="pc-new-category-select"
                value={catChoice}
                onChange={(e) => {
                  const v = e.target.value;
                  setCatChoice(v);
                  setForm({ ...form, category: v === "__new__" ? "" : v });
                }}
                className="w-full border border-[var(--border)] px-3 py-2 text-sm bg-white focus:outline-none focus:border-black"
              >
                <option value="">Sin categoría</option>
                {existingCategories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
                <option value="__new__">+ Nueva categoría…</option>
              </select>
              {catChoice === "__new__" && (
                <input
                  data-testid="pc-new-category"
                  autoFocus
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full border border-[var(--border)] px-3 py-2 text-sm mt-2 focus:outline-none focus:border-black"
                  placeholder="Nombre de la nueva categoría"
                />
              )}
            </div>
            <div className="sm:col-span-2">
              <label className="label-overline block mb-1">URL de foto (opcional)</label>
              <input
                data-testid="pc-new-photo"
                value={form.photo_url}
                onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
                className="w-full border border-[var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-black"
                placeholder="https://..."
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
              <span className="font-display font-bold">Activo</span>
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setCreating(false)}
              className="border border-[var(--border)] px-4 py-2 font-display font-bold text-sm hover:bg-[var(--surface-alt)]"
            >
              Cancelar
            </button>
            <button
              data-testid="pc-new-submit"
              type="submit"
              className="bg-black text-white px-4 py-2 font-display font-bold text-sm"
            >
              Crear producto
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="font-mono-tech text-sm text-[var(--text-secondary)] py-8 text-center">
          LOADING…
        </div>
      ) : products.length === 0 ? (
        <div className="border border-dashed border-[var(--border)] bg-white py-16 text-center text-[var(--text-secondary)]">
          Aún no hay productos. Creá el primero con &quot;+ Nuevo Producto&quot;.
        </div>
      ) : (
        <div className="space-y-5">
          <input
            data-testid="pc-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, categoría o tipo…"
            className="w-full border border-[var(--border)] bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-black"
          />
          {visibleProducts.length === 0 ? (
            <div
              data-testid="pc-search-empty"
              className="border border-dashed border-[var(--border)] bg-white py-12 text-center text-[var(--text-secondary)]"
            >
              Sin resultados para &quot;{search.trim()}&quot;.
            </div>
          ) : (
          groupProducts(visibleProducts).map(([cat, items]) => (
            <div key={cat} data-testid={`pc-category-group-${cat}`}>
              <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.2em] uppercase text-[var(--text-secondary)] mb-2">
                {cat} · {items.length}
              </div>
              <div className="border border-[var(--border)] bg-white divide-y divide-[var(--border)]">
                {items.map((p) => (
            <div
              key={p.id}
              data-testid={`pc-row-${p.id}`}
              className="grid grid-cols-12 gap-4 items-center px-5 py-4 hover:bg-[var(--bg)]"
            >
              <div
                className="col-span-2 cursor-pointer"
                data-testid={`pc-open-photo-${p.id}`}
                onClick={() => onOpenProduct?.(p.id)}
              >
                {p.photo_url ? (
                  <img
                    src={p.photo_url}
                    alt={p.name}
                    className="w-16 h-16 object-cover border border-[var(--border)]"
                  />
                ) : (
                  <div className="w-16 h-16 border border-dashed border-[var(--border)] flex items-center justify-center text-[10px] text-[var(--text-secondary)]">
                    Sin foto
                  </div>
                )}
              </div>
              <div
                className="col-span-4 cursor-pointer"
                data-testid={`pc-open-name-${p.id}`}
                onClick={() => onOpenProduct?.(p.id)}
              >
                <div className="font-display text-lg font-bold tracking-tight hover:underline">{p.name}</div>
                <div className="font-mono-tech text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">
                  {p.category || "Sin categoría"}
                </div>
              </div>
              <div className="col-span-3">
                <span
                  className={`inline-block border px-2 py-0.5 text-[10px] font-display font-bold tracking-wide ${
                    p.active
                      ? "bg-emerald-100 border-emerald-300 text-emerald-900"
                      : "bg-neutral-200 border-neutral-300 text-neutral-700"
                  }`}
                >
                  {p.active ? "Activo" : "Inactivo"}
                </span>
                <div className="font-mono-tech text-[10px] text-[var(--text-secondary)] mt-1">
                  Tipos: {(p.types || []).length} · Cantidades: {(p.quantities || []).length} · Extras: {(p.extras || []).length}
                </div>
              </div>
              <div className="col-span-3 flex justify-end gap-2">
                <button
                  data-testid={`pc-edit-${p.id}`}
                  onClick={() => onOpenProduct?.(p.id)}
                  className="border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--surface-alt)]"
                >
                  {isAdmin ? "Editar" : "Ver"}
                </button>
                {isAdmin && (
                  <button
                    data-testid={`pc-del-${p.id}`}
                    onClick={() => removeProduct(p.id)}
                    className="border border-[var(--alert-overdue)] text-[var(--alert-overdue)] px-3 py-1.5 text-sm hover:bg-[var(--alert-overdue)] hover:text-white"
                  >
                    Eliminar
                  </button>
                )}
              </div>
            </div>
                ))}
              </div>
            </div>
          ))
          )}
        </div>
      )}
    </div>
  );
}
